"use client";

import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { blurPlaceholders, getOptimalBlurPlaceholder } from "@/lib/blur-placeholder";

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  containerClassName?: string;
  priority?: boolean;
  quality?: number;
  sizes?: string;
  fill?: boolean;
  objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  blurType?: keyof typeof blurPlaceholders;
  onLoad?: () => void;
  loading?: "lazy" | "eager";
}

export const OptimizedImage = ({
  src,
  alt,
  width,
  height,
  className = "",
  containerClassName = "",
  priority = false,
  quality = 85,
  sizes,
  fill = false,
  objectFit = "cover",
  placeholder = "blur",
  blurDataURL,
  blurType = "default",
  onLoad,
  loading,
}: OptimizedImageProps) => {
  // Generate blur placeholder if not provided
  const defaultBlurDataURL = blurDataURL || 
    (blurType ? blurPlaceholders[blurType] : getOptimalBlurPlaceholder(width, height));
  const [isLoading, setIsLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Handle the case where the image might be cached and already loaded
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setIsLoading(false);
      setTimeout(() => setIsImageLoaded(true), 100);
    }
  }, []);

  const handleImageLoad = () => {
    setIsLoading(false);
    setTimeout(() => setIsImageLoaded(true), 100);
    onLoad?.();
  };

  const handleImageError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  // Generate responsive sizes if not provided
  const defaultSizes = fill
    ? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
    : sizes;

  // Determine object-fit class
  const objectFitClass = {
    contain: "object-contain",
    cover: "object-cover",
    fill: "object-fill",
    none: "object-none",
    "scale-down": "object-scale-down",
  }[objectFit];

  if (hasError) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-gray-100 dark:bg-gray-800",
          fill ? "absolute inset-0" : "",
          containerClassName
        )}
        style={{
          width: !fill && width ? `${width}px` : undefined,
          height: !fill && height ? `${height}px` : undefined,
        }}
      >
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Failed to load image
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        fill ? "absolute inset-0" : "",
        containerClassName
      )}
      style={{
        width: !fill && width ? `${width}px` : undefined,
        height: !fill && height ? `${height}px` : undefined,
      }}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
      )}

      {/* The actual image with smooth transition */}
      <motion.div
        className={cn("h-full w-full", fill ? "absolute inset-0" : "")}
        initial={false}
        animate={{
          opacity: isImageLoaded ? 1 : 0,
          scale: isImageLoaded ? 1 : 1.02,
        }}
        transition={{
          duration: 0.4,
          ease: "easeOut",
        }}
      >
        <Image
          ref={imgRef as React.Ref<HTMLImageElement>}
          src={src}
          alt={alt}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          fill={fill}
          sizes={defaultSizes}
          quality={quality}
          priority={priority}
          loading={loading || (priority ? "eager" : "lazy")}
          placeholder={placeholder}
          blurDataURL={placeholder === "blur" ? defaultBlurDataURL : undefined}
          className={cn(objectFitClass, className)}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            maxWidth: "100%",
            height: fill ? "100%" : "auto",
          }}
        />
      </motion.div>
    </div>
  );
};

// Export a static version for SEO-critical images
export const OptimizedImageStatic = ({
  src,
  alt,
  width,
  height,
  className = "",
  priority = true,
  quality = 85,
  sizes,
  fill = false,
  objectFit = "cover",
  placeholder = "blur",
  blurDataURL,
  blurType = "default",
}: Omit<OptimizedImageProps, "containerClassName" | "onLoad" | "loading">) => {
  const defaultBlurDataURL = blurDataURL || 
    (blurType ? blurPlaceholders[blurType] : getOptimalBlurPlaceholder(width, height));
  const objectFitClass = {
    contain: "object-contain",
    cover: "object-cover",
    fill: "object-fill",
    none: "object-none",
    "scale-down": "object-scale-down",
  }[objectFit];

  return (
    <Image
      src={src}
      alt={alt}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      fill={fill}
      sizes={sizes}
      quality={quality}
      priority={priority}
      placeholder={placeholder}
      blurDataURL={placeholder === "blur" ? defaultBlurDataURL : undefined}
      className={cn(objectFitClass, className)}
      style={{
        maxWidth: "100%",
        height: fill ? "100%" : "auto",
      }}
    />
  );
};